<script setup>
import { cloneDeep } from 'lodash-es';
import HawkTreeSelect from '~/common/components/organisms/hawk-tree/hawk-treeselect.vue';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import { useTerraStore } from '~/terra/store/terra.store';
import { useTerraHelperComposable } from '~/terra/utils/helper-composable.js';

const props = defineProps({
  functionCall: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['continue', 'cancel']);

const { $t } = useCommonImports();

const terra_store = useTerraStore();

const { getProjectOptions } = useTerraHelperComposable();

const form = ref({
  projects: [],
});
const is_loading = ref(false);

const projects_list = computed(() => getProjectOptions());

const group_project_names_by_uid = computed(() => {
  return projects_list.value.reduce((acc, layer) => {
    if (!acc[layer.name]) {
      acc[layer.name] = {};
    }
    layer.projects.forEach((sub) => {
      acc[layer.name][sub.name] = sub.uid;
    });
    return acc;
  }, {});
});

async function onContinue() {
  try {
    is_loading.value = true;
    const selected_sublayers = form.value.projects;
    const selected_layers_data = projects_list.value.reduce((acc, layer) => {
      const is_layer_visible = layer.projects.some(sub => selected_sublayers.includes(sub.uid));
      const active_layer = layer.projects.every(sub => selected_sublayers.includes(sub.uid));
      terra_store.active_group_map[layer.uid] = {
        ortho_enabled: active_layer,
        features_enabled: active_layer,
      };
      if (is_layer_visible) {
        acc.push({
          layer: layer.name,
          sublayers: layer.projects.filter(sub => selected_sublayers.includes(sub.uid)).map(sub => sub.name),
        });
      }
      layer.projects.forEach((sub) => {
        terra_store.active_projects_map[sub.uid] = {
          ortho_enabled: false,
          features_enabled: false,
        };
      });
      return acc;
    }, []);

    // Toggle projects
    const projects = [];
    selected_sublayers.forEach((project_uid) => {
      const project = terra_store.active_projects_data_map({ all_projects: true })[project_uid];
      terra_store.active_projects_map[project.uid] = {
        ortho_enabled: true,
        features_enabled: true,
      };
      projects.push(project);
    });
    await terra_store.set_projects_essentials({ projects });
    if (projects.length === 1) {
      terra_store.fly_to_project({ project: projects[0] });
    }
    terra_store.update_features_on_map_flag += 1;
    await terra_store.update_map_features_and_polygon();
    terra_store.is_loading = false;

    emit('continue', {
      response: [
        {
          message: 'Successfully toggled the layers.',
          selected_layers_data,
        },
      ],
    });
    is_loading.value = false;
  }
  catch (error) {
    logger.error('Error toggling layers:', error);
    emit('cancel', {
      response: [
        {
          message: 'Failed to toggle the layers.',
        },
      ],
    });
    is_loading.value = false;
  }
}

function onCancel() {
  emit('cancel', {
    response: [
      {
        message: 'User cancelled the action',
      },
    ],
  });
}

// Initialize form with empty projects
function getDefaultSelected() {
  const active_projects_map = cloneDeep(terra_store.active_projects_map);
  props.functionCall.arguments.layers.forEach(({ layer_name, sublayers }) => {
    if (group_project_names_by_uid.value[layer_name]) {
      sublayers.forEach((sublayer) => {
        const project_uid = group_project_names_by_uid.value[layer_name][sublayer];
        const is_visible = props.functionCall.arguments.visibility !== 'hide';
        if (project_uid) {
          active_projects_map[project_uid] = {
            ortho_enabled: is_visible,
            features_enabled: is_visible,
          };
        }
      });
    }
  });
  return Object.entries(active_projects_map)
    .filter(([_, project]) => project.ortho_enabled || project.features_enabled)
    .map(([uid, _]) => uid);
}
</script>

<template>
  <NaavixActionBox @continue="onContinue" @cancel="onCancel">
    <template #header>
      <div class="flex items-center gap-3">
        <HawkFeaturedIcon theme="light-circle" color="warning">
          <IconHawkAlertTriangle />
        </HawkFeaturedIcon>
        <div class="text-md font-semibold text-gray-900">
          {{ $t('Toggle layers') }}
        </div>
      </div>
    </template>
    <template #content>
      <div class="text-xs font-normal text-gray-600">
        {{ $t('Are you sure you want to toggle the layers?') }}
      </div>
      <Vueform
        v-model="form"
        size="sm"
        :columns="{
          default: { container: 12, label: 4, wrapper: 12 },
          sm: { container: 12, label: 4, wrapper: 12 },
          md: { container: 12, label: 4, wrapper: 12 },
        }"
        sync
        :display-errors="false"
      >
        <div class="col-span-12">
          <HawkTreeSelect
            class="my-5"
            :options="{
              name: 'projects',
              label: $t('Layers'),
              openDirection: 'top',
            }"
            select_type="LEAF_PRIORITY"
            children_key="projects"
            label_key="name"
            value_key="uid"
            :data="projects_list"
            :initial_state="getDefaultSelected()"
            @update-form="form.projects = $event"
          />
        </div>
      </Vueform>
    </template>
    <template #footer>
      <div class="flex gap-2 justify-end w-full pr-3 py-2">
        <HawkButton
          type="plain"
          :secondary="true"
          @click="onCancel"
        >
          {{ $t('Cancel') }}
        </HawkButton>
        <HawkButton
          type="link"
          :loading="is_loading"
          @click="onContinue"
        >
          {{ $t('Continue') }}
        </HawkButton>
      </div>
    </template>
  </NaavixActionBox>
</template>
